import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ListRenderItemInfo, Pressable, useWindowDimensions, View } from 'react-native'
import { IHandles } from 'react-native-modalize/lib/options'

import AddCircularIcon from '@common/assets/svg/AddCircularIcon'
import DownArrowIcon from '@common/assets/svg/DownArrowIcon'
import SettingsWheelIcon from '@common/assets/svg/SettingsWheelIcon'
import BottomSheet from '@common/components/BottomSheet'
import ModalHeader from '@common/components/BottomSheet/ModalHeader'
import Text from '@common/components/Text'
import { isMobile } from '@common/config/env'
import useController from '@common/hooks/useController'
import useControllersMiddleware from '@common/hooks/useControllersMiddleware'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import Account from '@common/modules/account-select/components/Account'
import { FEATURES } from '@web/constants/features'
import AddAccountTypeOptions, {
  AddAccountType
} from '@common/modules/account-select/components/AddAccountTypeOptions'
import useOnboardingNavigation from '@common/modules/auth/hooks/useOnboardingNavigation'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import alert from '@common/services/alert'
import spacings from '@common/styles/spacings'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

const SavedSeedPhrasesBottomSheet = ({
  sheetRef,
  open,
  close,
  handleClose
}: {
  sheetRef: React.RefObject<IHandles>
  open: (dest?: 'default' | 'top' | undefined) => void
  close: (dest?: 'default' | 'alwaysOpen' | undefined) => void
  handleClose: () => void
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { statuses } = useController('StorageController').state
  const { accounts } = useController('AccountsController').state
  const { seeds, keys } = useController('KeystoreController').state
  const { dispatch } = useControllersMiddleware()
  const { subType, initParams } = useController('AccountPickerController').state
  const { minWidthSize } = useWindowSize()
  const { height: windowHeight } = useWindowDimensions()
  const isNarrow = minWidthSize('s')
  const [addAccountButtonPressed, setAddAccountButtonPressed] = useState(false)
  // When set, the sheet shows the "Add account" chooser (Smart / Standard)
  // for this seed instead of the seed list
  const [chooserSeedId, setChooserSeedId] = useState<string | null>(null)
  const { goToNextRoute } = useOnboardingNavigation()
  const { navigate } = useNavigation()

  useEffect(() => {
    if (addAccountButtonPressed && initParams && subType === 'seed') {
      setAddAccountButtonPressed(false)
      setChooserSeedId(null)
      goToNextRoute(WEB_ROUTES.accountPersonalize)
    }
  }, [addAccountButtonPressed, goToNextRoute, initParams, subType])

  const getAccountsForSeed = useCallback(
    (seedId: string) => {
      const keysFromSeed = keys.filter((k) => k.meta.fromSeedId === seedId)
      const keysFromSeedAddr = keysFromSeed.map(({ addr }) => addr)
      return accounts.filter(
        (a) => !a.safeCreation && a.associatedKeys.some((k) => keysFromSeedAddr.includes(k))
      )
    },
    [keys, accounts]
  )

  const handleAddAddressFromSeed = useCallback(
    (id: string, nextAccountType: AddAccountType) => {
      setAddAccountButtonPressed(true)
      dispatch({
        type: 'MAIN_CONTROLLER_ACCOUNT_PICKER_INIT_FROM_SAVED_SEED_PHRASE',
        params: { id, nextAccountType }
      })
    },
    [dispatch]
  )

  const renderItem = ({
    item,
    index
  }: ListRenderItemInfo<
    ReturnType<typeof useController<'KeystoreController'>>['state']['seeds'][number]
  >) => {
    const seedAccounts = getAccountsForSeed(item.id) || []

    return (
      <View
        style={[
          index < seeds.length - 1 && spacings.mbTy,
          spacings.phSm,
          spacings.pvSm,
          {
            borderRadius: BORDER_RADIUS_PRIMARY,
            backgroundColor: theme.secondaryBackground
          }
        ]}
      >
        <View
          style={[
            flexbox.directionRow,
            flexbox.justifySpaceBetween,
            flexbox.alignCenter,
            spacings.mbSm
          ]}
        >
          <Text weight="medium" appearance="secondaryText" numberOfLines={1} style={spacings.plSm}>
            {item.label}
          </Text>
          <Pressable
            testID={`add-account-from-seed-${index}`}
            // SMART_ACCOUNT_ENABLED=false leaves the chooser with a single
            // "Standard account" row — skip the extra step and derive it directly.
            onPress={() =>
              FEATURES.smartAccountEnabled
                ? setChooserSeedId(item.id)
                : handleAddAddressFromSeed(item.id, 'basic')
            }
            style={[flexbox.directionRow, flexbox.alignCenter, spacings.pvMi, spacings.phTy]}
          >
            <AddCircularIcon width={20} height={20} color={theme.primary} style={spacings.mrMi} />
            <Text fontSize={14} weight="medium" color={theme.primary} style={spacings.mrMi}>
              {t('Add account')}
            </Text>
            <DownArrowIcon width={12} height={7} color={theme.primary} />
          </Pressable>
        </View>
        {seedAccounts.map((a) => {
          return (
            <Account
              key={a.addr}
              account={a}
              withSettings={false}
              isSelectable={false}
              withKeyType={false}
            />
          )
        })}
        {!seedAccounts.length && (
          <Text
            fontSize={14}
            weight="medium"
            appearance="secondaryText"
            style={[spacings.mvM, text.center]}
          >
            {item.id === 'legacy-saved-seed' &&
            statuses.associateAccountKeysWithLegacySavedSeedMigration !== 'INITIAL'
              ? t('Linking accounts to this recovery phrase. This may take a moment...')
              : t('No accounts added from this seed.')}
          </Text>
        )}
      </View>
    )
  }

  const handleCloseSheet = useCallback(() => {
    setChooserSeedId(null)
    handleClose()
  }, [handleClose])

  return (
    <BottomSheet
      id="seed-phrases-bottom-sheet"
      sheetRef={sheetRef}
      // One frame for both views (#551): the compact 480px card of the other
      // small popups. `overflow: hidden` keeps the list off the bottom corners.
      style={{ width: 480, maxWidth: '90%', overflow: 'hidden' } as any}
      closeBottomSheet={() => {
        setChooserSeedId(null)
        close()
      }}
      HeaderComponent={
        chooserSeedId ? (
          <ModalHeader
            compact={isNarrow}
            handleClose={() => setChooserSeedId(null)}
            title={t('Add account')}
          />
        ) : (
          <ModalHeader
            compact={isNarrow}
            handleClose={handleCloseSheet}
            title={t('Add from recovery phrase')}
          >
            <Pressable
              onPress={() => {
                if (isMobile) {
                  alert('Coming soon!')
                  return
                }
                navigate(WEB_ROUTES.recoveryPhrasesSettings)
              }}
            >
              {({ hovered }: any) => (
                <SettingsWheelIcon
                  width={28}
                  height={28}
                  color={hovered ? theme.primaryText : theme.iconPrimary}
                />
              )}
            </Pressable>
          </ModalHeader>
        )
      }
      flatListProps={
        chooserSeedId
          ? undefined
          : {
              data: seeds,
              renderItem,
              keyExtractor: (item) => item.id,
              // Below Modalize's max height, so the card stays centered like the
              // chooser instead of stretching into a short bottom sheet.
              style: { maxHeight: Math.round(windowHeight * 0.7) }
            }
      }
    >
      {chooserSeedId ? (
        <AddAccountTypeOptions
          onSelect={(accountType) => handleAddAddressFromSeed(chooserSeedId, accountType)}
        />
      ) : null}
    </BottomSheet>
  )
}

export default React.memo(SavedSeedPhrasesBottomSheet)
